import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard, ArrowLeftRight, CalendarDays, Boxes, Shirt, Users,
  BarChart3, FileText, Clock3, Settings, LogOut, Plus, Menu, PackageSearch, WashingMachine, Wallet, Receipt, TrendingUp,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useAuth } from '../context/AuthContext';

// Menu disusun mengikuti alur kerja harian, bukan menurut jenis halaman.
//   HARI INI  -> yang dilihat & dikerjakan tiap hari
//   BARANG    -> urusan koleksi dan stok
//   UANG      -> semua yang menyangkut kas dan laporan
const NAV = [
  { section: 'Hari Ini' },
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/transaksi', label: 'Order Sewa', icon: ArrowLeftRight },
  { to: '/kas', label: 'Kas & Pengeluaran', icon: Wallet, ownerOnly: true },
  { to: '/pemantauan', label: 'Pemantauan', icon: PackageSearch },
  { to: '/aktivitas', label: 'Aktivitas Hari Ini', icon: Clock3, ownerOnly: true },

  { section: 'Barang' },
  { to: '/booking', label: 'Booking', icon: CalendarDays },
  { to: '/laundry', label: 'Laundry', icon: WashingMachine },
  { to: '/stok', label: 'Stok', icon: Boxes },
  { to: '/koleksi', label: 'Koleksi', icon: Shirt },

  { section: 'Pelanggan' },
  { to: '/customer', label: 'Customer', icon: Users },
  { to: '/nota', label: 'Nota', icon: FileText },

  { section: 'Laporan' },
  { to: '/laporan', label: 'Laporan Umum', icon: BarChart3, ownerOnly: true },
  { to: '/laporan-pembayaran', label: 'Laporan Pembayaran', icon: Receipt, ownerOnly: true },
  { to: '/performa-koleksi', label: 'Performa Koleksi', icon: TrendingUp, ownerOnly: true },
  { to: '/pengaturan', label: 'Pengaturan', icon: Settings, ownerOnly: true },
];

function Brand({ compact = false }) {
  return (
    <div className="flex items-center gap-3">
      <img src="/logo.png" alt="Deyran Gallery" className={`${compact ? 'w-9 h-9' : 'w-11 h-11'} rounded-full object-cover ring-1 ring-gold/50`} />
      <div>
        <p className={`font-display ${compact ? 'text-base' : 'text-xl'} text-ivory tracking-wide leading-tight`}>DEYRAN GALLERY</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-gold/80 mt-0.5">Rental Kebaya & Baju Bodo</p>
      </div>
    </div>
  );
}

function Sidebar({ nav }) {
  const { logout, user } = useAuth();
  return (
    <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-choco flex-col z-40" data-testid="desktop-sidebar">
      <div className="absolute inset-y-0 right-0 w-1 songket-strip opacity-70" />
      <div className="px-6 pt-7 pb-6">
        <NavLink to="/" data-testid="brand-home-link" className="block">
          <Brand />
        </NavLink>
      </div>
      <nav className="flex-1 px-4 space-y-0.5 overflow-y-auto">
        {nav.map((n) =>
          n.section ? (
            <p key={`sec-${n.section}`} data-testid={`nav-section-${n.section.toLowerCase().replace(/\s+/g, '-')}`}
              className="px-4 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-gold/70 font-bold">
              {n.section}
            </p>
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}-link`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isActive ? 'bg-caramel/25 text-ivory font-semibold' : 'text-[#EDE4D8]/60 hover:bg-white/5 hover:text-ivory'
                }`
              }
            >
              <n.icon size={18} strokeWidth={1.8} />
              {n.label}
            </NavLink>
          )
        )}
      </nav>
      <div className="p-4 space-y-2">
        <div className="rounded-xl bg-white/5 px-4 py-2.5" data-testid="sidebar-user-info">
          <p className="text-xs font-semibold text-ivory/90">{user?.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-gold/70">{user?.role === 'owner' ? 'Owner' : 'Karyawan'}</p>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-[#EDE4D8]/50 hover:text-ivory hover:bg-white/5 transition-colors duration-150 w-full"
        >
          <LogOut size={18} strokeWidth={1.8} /> Keluar
        </button>
      </div>
    </aside>
  );
}

function MobileHeader() {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';
  return (
    <header className="md:hidden sticky top-0 z-40 bg-ivory/95 backdrop-blur border-b border-border" data-testid="mobile-header">
      <div className="px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <NavLink to="/" data-testid="mobile-brand-home-link" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Deyran Gallery" className="w-9 h-9 rounded-full object-cover ring-1 ring-gold/50" />
            <div>
              <p className="font-display text-base text-brown-dark leading-tight tracking-wide">DEYRAN GALLERY</p>
              <p className="text-[9px] uppercase tracking-[0.16em] text-mocha">Rental Kebaya & Baju Bodo</p>
            </div>
          </NavLink>
        </div>
        {isOwner && (
          <NavLink to="/pengaturan" data-testid="mobile-header-settings-link" className="p-2 rounded-xl bg-white border border-border text-brown">
            <Settings size={18} />
          </NavLink>
        )}
      </div>
    </header>
  );
}

function BottomNav({ moreItems }) {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const { logout } = useAuth();

  const itemCls = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-1 py-2 text-[10px] transition-colors duration-150 ${
      isActive ? 'text-brown font-bold' : 'text-mocha'
    }`;

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border shadow-[0_-4px_20px_rgba(59,40,28,0.08)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5">
          <NavLink to="/" end data-testid="bottom-nav-dashboard" className={itemCls}>
            <LayoutDashboard size={20} />Dashboard
          </NavLink>
          <NavLink to="/transaksi" data-testid="bottom-nav-transaksi" className={itemCls}>
            <ArrowLeftRight size={20} />Order
          </NavLink>
          <div className="flex items-start justify-center">
            <button
              data-testid="bottom-nav-quick-add"
              onClick={() => navigate('/transaksi/baru')}
              className="-mt-6 w-14 h-14 rounded-full bg-brown text-white flex items-center justify-center shadow-lift border-4 border-ivory active:scale-95 transition-transform duration-150"
            >
              <Plus size={26} />
            </button>
          </div>
          <NavLink to="/booking" data-testid="bottom-nav-booking" className={itemCls}>
            <CalendarDays size={20} />Booking
          </NavLink>
          <button
            data-testid="bottom-nav-more"
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center justify-center gap-1 py-2 text-[10px] text-mocha"
          >
            <Menu size={20} />Lainnya
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl bg-ivory" data-testid="more-menu-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-brown-dark">Menu Lainnya</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-3 py-4">
            {moreItems.map((n) => (
              <button
                key={n.to}
                data-testid={`more-menu-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => {
                  setMoreOpen(false);
                  navigate(n.to);
                }}
                className="flex flex-col items-center gap-2 rounded-2xl bg-white border border-border p-4 text-brown text-xs font-semibold shadow-soft"
              >
                <n.icon size={22} />
                {n.label}
              </button>
            ))}
          </div>
          <button
            data-testid="more-menu-logout"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-danger/30 text-danger py-3 text-sm font-semibold"
          >
            <LogOut size={16} /> Keluar
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';
  const terpakai = isOwner ? NAV : NAV.filter((n) => !n.ownerOnly);
  // Buang judul kelompok yang jadi kosong setelah menu owner disembunyikan.
  const nav = terpakai.filter((n, i) => !n.section || terpakai.slice(i + 1).find((x) => x.section || x.to)?.to);
  const flatNav = nav.filter((n) => n.to);
  const moreItems = flatNav.filter((n) => ['/stok', '/pemantauan', '/laundry', '/koleksi', '/customer', '/laporan', '/laporan-pembayaran', '/kas', '/nota', '/aktivitas', '/pengaturan'].includes(n.to));

  return (
    <div className="min-h-screen bg-ivory">
      <Sidebar nav={nav} />
      <MobileHeader />
      <main className="md:ml-64 px-4 md:px-8 py-5 md:py-8 pb-28 md:pb-10 page-enter">
        <Outlet />
      </main>
      <BottomNav moreItems={moreItems} />
    </div>
  );
}
